import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "", contact_type: "general" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all required fields."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground">
              Contact
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-6 max-w-xl mx-auto leading-relaxed">
              If you wish to connect, please fill out the form below. Whether you have a question, a reflection, or simply want to reach out, you are welcome.
            </p>
          </div>
        </FadeIn>
      </section>

      <section className="pb-24 md:pb-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          {/* Email */}
          <FadeIn>
            <div className="flex items-center gap-3 mb-12 justify-center">
              <Mail size={16} strokeWidth={1.5} className="text-[#5C6B5D]" />
              <a href="mailto:bikram@jdf.org.in" className="font-body text-base text-foreground hover:text-[#5C6B5D] transition-colors" data-testid="contact-email-link">
                bikram@jdf.org.in
              </a>
            </div>
          </FadeIn>

          {/* Form */}
          <FadeIn>
            {submitted ? (
              <div className="p-8 border border-[#5C6B5D]/30 text-center" data-testid="contact-success">
                <h4 className="font-heading text-xl text-foreground mb-2">Thank you.</h4>
                <p className="font-body text-sm text-muted-foreground">We will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label className="font-body text-sm text-foreground">Type</Label>
                  <Select value={form.contact_type} onValueChange={(v) => update("contact_type", v)}>
                    <SelectTrigger className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none font-body text-sm focus:ring-0" data-testid="contact-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
                      <SelectItem value="general" className="font-body text-sm">General Inquiry</SelectItem>
                      <SelectItem value="collaboration" className="font-body text-sm">Collaboration & Partnership</SelectItem>
                      <SelectItem value="volunteer" className="font-body text-sm">Volunteer</SelectItem>
                      <SelectItem value="hosting" className="font-body text-sm">Host a Dialogue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-body text-sm text-foreground">Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-sm text-foreground">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D]"
                      placeholder="Your email"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-foreground">Message *</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="mt-2 bg-transparent border-0 border-b border-[hsl(48,12%,88%)] rounded-none px-0 py-3 font-body focus-visible:ring-0 focus-visible:border-[#5C6B5D] min-h-[120px] resize-none"
                    placeholder="Share your thoughts..."
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </FadeIn>

          <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-16" />

          {/* Collaboration */}
          <FadeIn>
            <div className="text-center">
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4 text-foreground">Collaboration & Partnership</h3>
              <div className="font-body text-base text-muted-foreground leading-relaxed space-y-4 max-w-xl mx-auto">
                <p>
                  If you are interested in collaborating, volunteering, hosting a dialogue, supporting a program, or exploring partnership in any of our areas of work, please write to us.
                </p>
                <p>
                  There is no formal barrier. No hesitation needed. We are friends in inquiry and action.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
